import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Truck, Beer, Wine, MapPin, Award, FileText } from 'lucide-react';
import useSEO, { SEO_CONFIG } from '@/hooks/useSEO';

const ESTABLISHMENTS = [
  'Restaurants',
  'Bars',
  'Campings',
  'Épiceries fines',
  'Cavistes',
  'Associations',
  'Comités des fêtes',
  'Entreprises',
];

const HIGHLIGHTS = [
  { icon: Truck, title: 'Livraison les mardis et jeudis', text: 'Une logistique régulière pour vous réapprovisionner sereinement.' },
  { icon: Beer, title: 'Fûts de 30 litres', text: 'Format professionnel idéal pour la pression dans votre établissement.' },
  { icon: Wine, title: 'Tireuses pour vos événements', text: 'Mise à disposition de tireuses à bière pour vos animations.' },
  { icon: MapPin, title: 'Brassées à Saint-Rémy-de-Provence', text: 'Une bière locale, artisanale, au cœur des Alpilles.' },
  { icon: Award, title: 'Médaillées au Concours International de Lyon', text: 'Une qualité reconnue par les professionnels du secteur.' },
];

export default function Professionnels() {
  useSEO(SEO_CONFIG.professionnels);

  return (
    <div>
      <section className="alpilles-topo-bg py-24 lg:py-28">
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-accent font-semibold tracking-wide uppercase text-sm mb-4">Espace Professionnels</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white mb-6 leading-tight">
            Une bière artisanale locale pour votre établissement
          </h1>
          <p className="text-lg text-white/80 max-w-3xl mx-auto mb-10">
            Restaurateurs, cavistes, bars, campings, comités des fêtes : faites découvrir à vos clients
            une bière artisanale brassée dans les Alpilles, à Saint-Rémy-de-Provence.
          </p>
          <Link to="/contact?sujet=Demande%20de%20grille%20tarifaire%20professionnels">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-7 text-lg font-semibold" data-testid="pro-hero-devis-button">
              <FileText className="w-5 h-5 mr-2" />
              Demander notre grille tarifaire
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading text-foreground mb-4">Pour qui ?</h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Nous accompagnons une grande variété d'établissements et d'organisations.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ESTABLISHMENTS.map((item) => (
              <div
                key={item}
                className="bg-secondary border border-border rounded-xl px-4 py-6 text-center font-medium text-foreground hover:border-primary/30 hover:shadow-md transition-all"
                data-testid={`establishment-${item.toLowerCase().replace(/ /g, '-')}`}
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading text-foreground mb-4">Nos atouts pour les professionnels</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {HIGHLIGHTS.map((h) => {
              const Icon = h.icon;
              return (
                <div key={h.title} className="bg-white border border-stone-200 rounded-2xl p-8 hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-heading text-foreground mb-2">{h.title}</h3>
                  <p className="text-foreground/70">{h.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-heading text-foreground mb-4">Devenez partenaire</h2>
          <p className="text-foreground/70 mb-8">
            Contactez-nous pour recevoir notre grille tarifaire professionnelle et discuter de vos besoins.
          </p>
          <Link to="/contact?sujet=Demande%20de%20grille%20tarifaire%20professionnels">
            <Button className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-10 py-7 text-lg font-semibold" data-testid="pro-bottom-devis-button">
              <FileText className="w-5 h-5 mr-2" />
              Demander notre grille tarifaire
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
